import * as React from 'react';
import { ChangeEvent } from '../../../hooks/useForm';
import { Answer, PacketFormValues } from '../../../hooks/useSellersPacketForm';
import { FComponent } from '../../../types/common';
import { PacketQuestion } from '../../../types/packet.schema';
import { Button } from '../Button';
import { Checkbox } from '../Form';
import AddIcon from '../Icons/AddIcon';
import { PacketFormField } from './PacketFormField';
import LockIcon from '../Icons/LockIcon';

type PacketFormQuestionsProps = {
  questions: PacketQuestion[];
  answers: Answer[];
  values: PacketFormValues;
  errors: Record<string, string[]>;
  handleFieldChange: (event: React.ChangeEvent<HTMLInputElement> | ChangeEvent) => void;
  isFieldVisited: (fielName: string) => boolean;
  addAnswer: (question: PacketQuestion) => void;
  removeAnswer: (id: string) => void;
  reuseFlags: Record<string, boolean>;
  setReuseFlags: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isIntegration: boolean;
};

const PacketFormQuestions: FComponent<PacketFormQuestionsProps> = ({
  questions,
  answers,
  values,
  errors,
  handleFieldChange,
  isFieldVisited,
  addAnswer,
  removeAnswer,
  reuseFlags,
  setReuseFlags,
  isIntegration
}) => {
  return (
    <>
      <div className="questions">
        {questions.map(question => (
          <div key={question.id} className="question">
            <div className="question-head">
              <div className="question-text">
                {question.text} {question.encrypted ? <LockIcon /> : null}
              </div>
              {question.subtext ? (
                <div className="question-subtext">{question.subtext}</div>
              ) : null}
            </div>
            <div className="question-answers">
              {answers
                .filter(answer => answer.questionId === question.id)
                .map((answer, _, answers) => (
                  <PacketFormField
                    key={answer.id}
                    question={question}
                    name={answer.id}
                    handleChange={handleFieldChange}
                    value={values[answer.id]}
                    acceptedFileExtensions={question.acceptedFileExtensions}
                    maxSize={question.maxFileSizeBytes}
                    minSize={question.minFileSizeBytes}
                    onDelete={answers.length > 1 ? removeAnswer : undefined}
                    errors={errors}
                    isFieldVisited={isFieldVisited}
                    disabled={isIntegration}
                  />
                ))}
            </div>
            {isExpanding(question, answers, isIntegration) ? (
              <Button
                className="add-answer-button"
                secondary
                icon={<AddIcon />}
                onClick={() => addAnswer(question)}>
                Add One More Answer
              </Button>
            ) : null}
            {question.matchingQuestionsCount > 0 && question.htmlInputType !== 'file' ? (
              <div className="reuse-checkbox">
                <Checkbox
                  name={question.id}
                  className="checkbox-default right full light"
                  checked={reuseFlags[question.id]}
                  onChange={e => {
                    e.persist();
                    setReuseFlags(prev => ({
                      ...prev,
                      [question.id]: e.target.checked
                    }));
                  }}
                  label={`Use ${question.multiple ? 'these' : 'this'} value${
                    question.multiple ? 's' : ''
                  } for ${question.matchingQuestionsCount} matching question${
                    question.matchingQuestionsCount > 1 ? 's' : ''
                  } in other packets`}
                />
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </>
  );
};

export { PacketFormQuestions };

const isExpanding = (
  question: PacketQuestion,
  answers: Answer[],
  isIntegration: boolean
) =>
  !isIntegration &&
  question.multiple &&
  question.boundedChoices.length === 0 &&
  answers.filter(answer => answer.questionId === question.id).length < 20;
